import { registerLocaleData } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
    withXhr
} from '@angular/common/http';
import localeBg from '@angular/common/locales/bg';
import localeDe from '@angular/common/locales/de';
import localeZa from '@angular/common/locales/en-ZA';
import { ErrorHandler, inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
    RouterModule
} from '@angular/router';
import { ReplaceUndefinedWithNullInterceptor } from '@common/interceptors/replace-undefined.interceptor';
import { KendoMessageService } from '@common/services/kendoMessage.service';
import { PcsOidcConfigLoader } from '@common/services/oidc-config-loader.service';
import { OidcLocalStorageService } from '@common/services/oidc-local-storage.service';
import { StartupService } from '@common/services/startup.service';
import { OpenDialogRegistry } from '@common/services/tracked-dialog.service';
import { USER_IDLE_CONFIG } from '@common/services/user-idle.service';
import { UserService } from '@common/services/user.service';
import { LoadingBarModule, LoadingBarService } from '@ngx-loading-bar/core';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IntlModule } from '@progress/kendo-angular-intl';
import '@progress/kendo-angular-intl/locales/bg/all';
import '@progress/kendo-angular-intl/locales/en-ZA/all';
import { MessageService } from '@progress/kendo-angular-l10n';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { UploadModule } from '@progress/kendo-angular-upload';
import { AbstractSecurityStorage, AuthModule, StsConfigLoader } from 'angular-auth-oidc-client';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { AppComponent } from './app.component';
import { ROUTES } from './app.routing';
import { CommonModule } from './common/common.module';
import { GlobalErrorHandler } from './common/global-error-handler';
import { AuthGuard } from './common/guards/auth.guard';
import { AuthorizationGuard } from './common/guards/authorization.guard';
import { HomeComponent } from './home.component';

registerLocaleData(localeDe);
registerLocaleData(localeZa);
registerLocaleData(localeBg, 'bg');

export function appInitFactory(startupService: StartupService): () => any {
    return () => {
        startupService.init();
    };
}

@NgModule({
    declarations: [AppComponent, HomeComponent],
    exports: [
        FormsModule,
        ReactiveFormsModule,
        LoadingBarModule,
        BrowserAnimationsModule,
        RouterModule,
        DateInputsModule,
        InputsModule
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        NotificationModule,
        FormsModule,
        ReactiveFormsModule,
        LoadingBarModule,
        RouterModule.forRoot(ROUTES, {
            initialNavigation: 'disabled',
            // Angular 22 defaults to 'always' (child routes inherit parent params/data); keep the pre-22 behaviour
            paramsInheritanceStrategy: 'emptyOnly'
        }),
        CommonModule,
        UploadModule,
        DateInputsModule,
        InputsModule,
        IntlModule,
        // the OIDC configuration is loaded at run time by AuthenticationService.configureOidc()
        AuthModule.forRoot({ loader: { provide: StsConfigLoader, useExisting: PcsOidcConfigLoader } })
    ],
    providers: [
        // ngx-translate 18 has no TranslateModule any more: the service and the http loader are provided
        // here, the pipe and directive are imported by CommonModule. Same /assets/i18n/<lang>.json as before.
        provideTranslateService({
            loader: provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json' })
        }),
        UserService,
        {
            provide: LOCALE_ID,
            deps: [UserService],
            useFactory: (userService: UserService) => {
                return userService.getUserLanguage() === 'bg' ? 'bg-BG' : 'en-ZA';
            }
        },
        { provide: MessageService, useClass: KendoMessageService },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: AbstractSecurityStorage, useClass: OidcLocalStorageService },
        // 13 hours without user activity log the user out (login component, AuthorizationGuard)
        { provide: USER_IDLE_CONFIG, useValue: { idle: 46800 } },
        provideEnvironmentNgxMask(),
        StartupService,
        provideAppInitializer(() => {
            const initializerFn = appInitFactory(inject(StartupService));
            return initializerFn();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ReplaceUndefinedWithNullInterceptor,
            multi: true
        },
        DialogRef,
        provideHttpClient(withXhr(), withInterceptorsFromDi())
    ]
})
export class AppModule {
    constructor(
        private authGuard: AuthGuard,
        private authorizationGuard: AuthorizationGuard,
        private router: Router,
        private translate: TranslateService,
        private http: HttpClient,
        private loadingBarService: LoadingBarService,
        openDialogRegistry: OpenDialogRegistry
    ) {
        const routerRef = this.loadingBarService.useRef('router');
        router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                routerRef.start();
                // Dialogs live outside the routed views; browser Back/Forward must not leave them behind
                if (event.navigationTrigger === 'popstate') {
                    openDialogRegistry.closeAll();
                }
            } else if (event instanceof NavigationError) {
                routerRef.complete();
                console.error(event.error);
            } else if (event instanceof NavigationEnd) {
                routerRef.complete();
            } else if (event instanceof NavigationCancel) {
                routerRef.complete();
            }
        });

        const defaultLang = 'en';
        // this language will be used as a fallback when a translation isn't found in the current language
        translate.setFallbackLang(defaultLang);

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        translate.use(defaultLang);
    }
}
